import { AnimatePresence, motion } from 'framer-motion';
import type { ReactElement, RefObject } from 'react';
import { dataEl, type DataElScope } from '@/utils/dataEl';
import { POPOVER_MIN_WIDTH, POPOVER_MOTION_KEY_PREFIX, TABLE_BACKDROP_BLUR, } from './Cmarkdowntable.constants';
import { CmarkdowntablePopoverEntries } from './CmarkdowntablePopoverEntries';
import { CmarkdowntablePopoverHeader } from './CmarkdowntablePopoverHeader';
import { CmarkdowntablePopoverResizeHandle } from './CmarkdowntablePopoverResizeHandle';
import { resolveCmarkdowntablePopoverView } from './CmarkdowntablePopoverView';
import { Cportal } from '../Cportal';
import type { CmarkdowntablePopoverEntry, CmarkdowntablePopoverPosition, CmarkdowntablePopoverRect, TablePopoverPointerHandler, } from './Cmarkdowntable.types';
interface CmarkdowntablePopoverProps {
    dataElScope: DataElScope;
    activeRowIndex: number | null;
    entries: CmarkdowntablePopoverEntry[] | null;
    idValue: string;
    position: CmarkdowntablePopoverPosition | null;
    manualRect: CmarkdowntablePopoverRect | null;
    didCopy: boolean;
    vh: number;
    popoverRef: RefObject<HTMLDivElement | null>;
    onCopyEntries: () => void;
    onClose: () => void;
    onHeaderPointerDown: TablePopoverPointerHandler;
    onHeaderPointerMove: TablePopoverPointerHandler;
    onHeaderPointerEnd: TablePopoverPointerHandler;
    onResizePointerDown: TablePopoverPointerHandler;
    onResizePointerMove: TablePopoverPointerHandler;
    onResizePointerEnd: TablePopoverPointerHandler;
}
export function CmarkdowntablePopover({ dataElScope, activeRowIndex, entries, idValue, position, manualRect, didCopy, vh, popoverRef, onCopyEntries, onClose, onHeaderPointerDown, onHeaderPointerMove, onHeaderPointerEnd, onResizePointerDown, onResizePointerMove, onResizePointerEnd, }: CmarkdowntablePopoverProps): ReactElement {
    const view = resolveCmarkdowntablePopoverView({
        activeRowIndex,
        entries,
        position,
        manualRect,
    });
    return (<Cportal>
      <AnimatePresence initial={false}>
        {view && (
            <motion.div data-el={dataEl(dataElScope, 'Popover/Root')} ref={popoverRef} key={`${POPOVER_MOTION_KEY_PREFIX}-${view.activeRowIndex}`} initial={{ opacity: 0, y: 6, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -4, scale: 0.98 }} transition={{ duration: 0.18, ease: [0.2, 0.8, 0.2, 1] }} style={{
                position: 'fixed',
                left: view.panelFrame.left,
                top: view.panelFrame.top,
                width: view.panelFrame.width,
                height: view.panelFrame.height,
                zIndex: 10001,
                minWidth: POPOVER_MIN_WIDTH,
                display: 'flex',
                flexDirection: 'column',
            }}>
              <div style={{
                flex: 1,
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                borderRadius: 14,
                backgroundImage: [
                    'radial-gradient(ellipse 300px 150px at 100% 0%, rgba(102,254,193,0.06), transparent 60%)',
                    'linear-gradient(180deg, rgba(8,22,16,0.98) 0%, rgba(5,14,10,0.98) 100%)',
                ].join(', '),
                boxShadow: [
                    'inset 0 0 0 1px rgba(255,255,255,0.08)',
                    '0 30px 70px -8px rgba(0,0,0,0.8)',
                    '0 0 0 1px rgba(0,0,0,0.4)',
                ].join(', '),
                color: '#fff',
                overflow: 'hidden',
                backdropFilter: TABLE_BACKDROP_BLUR,
                WebkitBackdropFilter: TABLE_BACKDROP_BLUR,
            }}>
                <CmarkdowntablePopoverHeader dataElScope={dataElScope} idValue={idValue} didCopy={didCopy} onCopyEntries={onCopyEntries} onClose={onClose} onHeaderPointerDown={onHeaderPointerDown} onHeaderPointerMove={onHeaderPointerMove} onHeaderPointerEnd={onHeaderPointerEnd}/>

                <CmarkdowntablePopoverEntries dataElScope={dataElScope} entries={view.entries} panelHeight={view.panelFrame.height} vh={vh}/>

                <CmarkdowntablePopoverResizeHandle dataElScope={dataElScope} onResizePointerDown={onResizePointerDown} onResizePointerMove={onResizePointerMove} onResizePointerEnd={onResizePointerEnd}/>
              </div>
            </motion.div>
          )}
      </AnimatePresence>
    </Cportal>);
}
